import { Flex, Box } from 'components/box';
import { Heading, Text } from 'components/typography';
import { Money, ProductInterface } from 'types/types';
import { css } from '@emotion/react';
import { useVodapayActive } from 'hooks/app';
import { isPhysical as isPhysicalType } from 'types/guards/product';
import ProductInfo from 'components/_pages/products/info';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import MoreInfo from 'components/_shared/elements/more-info';

const CustomerDeliveryTimeModal = dynamic(
  () => import('components/_pages/products/modals/customer-delivery-time')
);

const ProductPricing = ({
  product,
  displayPrice,
  hidePriceRange,
  isTimedOut,
  canShowProduct = true,
  openParallelImportModal,
}: {
  product: ProductInterface;
  displayPrice?: Money;
  hidePriceRange?: boolean;
  isTimedOut?: boolean;
  canShowProduct?: boolean;
  openParallelImportModal?: () => void;
}) => {
  let isPhysical = false;
  let isFreeShipping = false;

  if (isPhysicalType(product)) {
    isPhysical = true;
    isFreeShipping = product.isFreeShipping;
  }

  const isVodapayActive = useVodapayActive();
  const [isDeliveryTimeModalOpen, setIsDeliveryTimeModalOpen] = useState(false);

  const { hasPriceRange } = product;
  const price = displayPrice || product.price;

  const showDeliveryTime =
    !!product?.customerDeliveryTime?.label &&
    !!product?.customerDeliveryTime?.description;

  return (
    <>
      <Box
        pb={[3, null, 2]}
        pt={[3, null, 0]}
        pr="0"
        my={[2, 0]}
        css={theme => css`
          ${canShowProduct &&
          !isTimedOut &&
          css`
            ${!product.externalListingLink &&
            css`
              border-top: 1px solid ${theme.colors.darkishGrey};
              border-bottom: 1px solid ${theme.colors.darkishGrey};
            `}

            @media ${theme.mediaQueries.desktopUp} {
              border-bottom: 1px solid ${theme.colors.darkishGrey};
            }
          `}

          @media ${theme.mediaQueries.desktopUp} {
            display: grid;
            gap: 2rem;
            grid-template-columns: auto auto;
            border-top: none;
          }
        `}
      >
        <Box className="show-for-desktop">
          {hasPriceRange && !hidePriceRange && (
            <Text color="black" mb={[0, 1]}>
              From:
            </Text>
          )}

          <Heading
            id="product-price"
            mb={hidePriceRange ? 0 : 1}
            fontSize="x3l"
            fontWeight={700}
            css={css`
              text-indent: -3px;
            `}
          >
            {hasPriceRange && !hidePriceRange
              ? product.minPrice.formattedValue
              : price.formattedValue}
          </Heading>

          {(!hidePriceRange || !hasPriceRange) &&
            product.retailPrice.value > 0 && (
              <Text
                color="darkGrey"
                mb={1}
                fontFamily="header"
                fontWeight="400"
                fontSize={[1, 4]}
                css={css`
                  text-decoration: line-through;
                `}
              >
                {product.retailPrice.formattedValue}
              </Text>
            )}
        </Box>

        {!isTimedOut && canShowProduct && !product.externalListingLink && (
          <Flex
            justifyContent="flex-start"
            alignItems={['flex-start', null, 'flex-end']}
            flexDirection="column"
            textAlign={['left', null, 'right']}
            alignSelf="flex-end"
            lineHeight="1em"
            css={css`
              ${!product.isGiftVoucher &&
              css`
                gap: 0.25rem;
              `}
            `}
          >
            {(isPhysical || !isVodapayActive) && (
              <Text fontSize={[1, 2, 1]} lineHeight="1.5em" color="darkGrey">
                {isPhysical && (
                  <>
                    {isFreeShipping ? 'Free' : 'Excludes'} shipping
                    <br />
                  </>
                )}

                {/**
                 * TODO: was asked to remove, but leaving it commented out
                 * coz I have good money saying people will change their minds later.
                 **/}
                {/* {!isVodapayActive && (
                  <>
                    Discovery Miles / eB:
                    <Price
                      currencyCode="EBUCKS"
                      hideSymbol
                      value={price.value}
                    />
                  </>
                )} */}
              </Text>
            )}

            {showDeliveryTime && (
              <Flex flexDirection="column">
                {product?.customerDeliveryTime?.label &&
                  product?.customerDeliveryTime?.description && (
                    <MoreInfo
                      label={
                        product.isGiftVoucher
                          ? product.customerDeliveryTime.label
                          : `ETA: ${product.customerDeliveryTime.label}`
                      }
                      callback={
                        !product.isGiftVoucher
                          ? () => setIsDeliveryTimeModalOpen(true)
                          : undefined
                      }
                    />
                  )}
              </Flex>
            )}
          </Flex>
        )}
      </Box>

      <ProductInfo
        product={product}
        openParallelImportModal={openParallelImportModal}
      />

      {showDeliveryTime && !!product?.customerDeliveryTime?.description && (
        <CustomerDeliveryTimeModal
          close={() => setIsDeliveryTimeModalOpen(false)}
          isOpen={isDeliveryTimeModalOpen}
          description={product.customerDeliveryTime.description}
        />
      )}
    </>
  );
};

export default ProductPricing;
